/** Signed-in principal, capabilities and branding — DEMO version with hash token pickup. */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, login as apiLogin, setTokens, setUnauthenticatedHandler } from '@/lib/api/client';
import { demoLogin as apiDemoLogin } from '@/lib/api/client-demo-addon';
import type { Branding, LoginResponse } from '@/lib/api/types';

export interface DemoRole {
  slug: string;
  label: string;
  description: string;
  color: string;
  email: string;
}

export interface DemoInfo {
  demo_mode: boolean;
  duration_minutes: number;
  remaining_minutes: number;
  auto_reset: boolean;
  roles: DemoRole[];
  features: Record<string, boolean>;
}

interface SessionValue {
  user: LoginResponse | null;
  branding: Branding | null;
  demoInfo: DemoInfo | null;
  demoRole: string | null;
  demoBootstrapping: boolean;
  signIn: (email: string, password: string, mfaCode?: string) => Promise<void>;
  demoSignIn: (role: string) => Promise<void>;
  signOut: () => void;
  can: (capability: string) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);
const DEMO_TOKEN_KEY = 'konnextions.demo.token';
const DEMO_ROLE_KEY = 'konnextions.demo.role';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<LoginResponse | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [demoInfo, setDemoInfo] = useState<DemoInfo | null>(null);
  const [demoRole, setDemoRole] = useState<string | null>(null);
  const [demoBootstrapping, setDemoBootstrapping] = useState(
    () => window.location.hash.startsWith('#demo?') || Boolean(sessionStorage.getItem(DEMO_TOKEN_KEY)),
  );

  const signOut = useCallback(() => {
    setTokens(null);
    setUser(null);
    setBranding(null);
    setDemoInfo(null);
    setDemoRole(null);
    setDemoBootstrapping(false);
    sessionStorage.removeItem(DEMO_TOKEN_KEY);
    sessionStorage.removeItem(DEMO_ROLE_KEY);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(signOut);
  }, [signOut]);

  const hashChecked = useRef(false);
  useEffect(() => {
    if (hashChecked.current || user) return;
    hashChecked.current = true;
    const hash = window.location.hash;
    const params = hash.startsWith('#demo?') ? new URLSearchParams(hash.slice(6)) : null;
    const token = params?.get('access_token') ?? sessionStorage.getItem(DEMO_TOKEN_KEY);
    const role = params?.get('role') ?? sessionStorage.getItem(DEMO_ROLE_KEY);
    if (!token || !role) {
      setDemoBootstrapping(false);
      return;
    }
    if (params) {
      window.location.hash = '';
      sessionStorage.setItem(DEMO_TOKEN_KEY, token);
      sessionStorage.setItem(DEMO_ROLE_KEY, role);
    }
    setTokens({ accessToken: token, refreshToken: token });
    (async () => {
      try {
        const status = await api.get<DemoInfo>('/demo/status');
        setDemoInfo(status);
        setDemoRole(role);
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          setUser({
            access_token: token, refresh_token: token, token_type: 'bearer',
            expires_in: status.duration_minutes * 60, user_id: payload.sub,
            organization_id: payload.org || null, full_name: payload.name || role,
            email: payload.email || '', roles: payload.roles || [role],
            capabilities: payload.capabilities || [],
          } as LoginResponse);
        }
        const brand = await api.get<Branding>('/meta/branding');
        setBranding(brand);
        if (brand.browser_title) document.title = brand.browser_title;
      } catch {
        signOut();
      } finally {
        setDemoBootstrapping(false);
      }
    })();
  }, [user, signOut]);

  const signIn = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const result = await apiLogin(email, password, mfaCode);
    setUser(result);
    try {
      const brand = await api.get<Branding>('/meta/branding');
      setBranding(brand);
      if (brand.browser_title) document.title = brand.browser_title;
    } catch {}
  }, []);

  const demoSignIn = useCallback(async (role: string) => {
    const result = await apiDemoLogin(role);
    const demoResult = result as LoginResponse & { demo?: DemoInfo };
    setUser(result);
    setDemoRole(role);
    sessionStorage.setItem(DEMO_TOKEN_KEY, result.access_token);
    sessionStorage.setItem(DEMO_ROLE_KEY, role);
    if (demoResult.demo) setDemoInfo(demoResult.demo);
    try {
      const brand = await api.get<Branding>('/meta/branding');
      setBranding(brand);
      if (brand.browser_title) document.title = brand.browser_title;
    } catch {}
  }, []);

  const can = useCallback(
    (capability: string) => Boolean(user?.capabilities.includes(capability)),
    [user],
  );

  const value = useMemo(
    () => ({ user, branding, demoInfo, demoRole, demoBootstrapping, signIn, demoSignIn, signOut, can }),
    [user, branding, demoInfo, demoRole, demoBootstrapping, signIn, demoSignIn, signOut, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
